import { Award, BookOpen, ChevronLeft, FileSpreadsheet, Loader2, PenSquare, Printer } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../../components/ui/Modal';
import { useAuth } from '../../contexts/AuthContext';
import { useModal } from '../../hooks/useModal';
import { ApiService } from '../../services/api';
import type { PracticeExam } from '../../types/practice-exam';
import LGSOptikForm from './LGSOptikForm';
import { getStudentReportData, StudentReportCard } from './ReportCards';
import YKSOptikForm from './YKSOptikForm';

type ViewMode = 'list' | 'solve' | 'result';

type ExamSolvePayload = {
  kitapcikTuru?: 'A' | 'B' | null;
  answers: Record<string, (string | null)[]>;
  branchSelections?: Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>;
};

const normalizeText = (value: string = '') =>
  value
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const resolveQuestionCountOverrides = (exam: PracticeExam | null) => {
  if (!exam?.bookletMapping) return {};

  const overrides: Record<string, number> = {};
  const isLgs = exam.levelType === 'lgs';

  Object.values(exam.bookletMapping || {}).forEach((entry: any) => {
    const questionCount = Number(entry?.questionCount || 0);
    if (!questionCount) return;

    const name = normalizeText(entry?.testName || '');

    if (name === 'turkce') overrides.turkce = questionCount;
    else if (name === 'sosyal bilgiler') {
      if (isLgs) overrides.sosyal = questionCount;
      else overrides.sosyal_bilimler = 20;
    }
    else if (name === 'temel matematik') overrides.temel_matematik = questionCount;
    else if (name === 'matematik') overrides.matematik = questionCount;
    else if (name === 'fen bilimleri') {
      if (isLgs) overrides.fen = questionCount;
      else overrides.fen_bilimleri_ayt = questionCount;
    }
    else if (name.includes('edebiyati') || name.includes('edebiyat')) overrides.edebiyat_sosyal1 = questionCount;
    else if (name.includes('sosyal bilimler 2')) overrides.sosyal_bilimler2 = 40;
    else if (name.includes('din')) overrides.din = questionCount;
    else if (name.includes('ingilizce')) overrides.ingilizce = questionCount;
    else if (name.includes('sosyal bilg') || name === 'sosyal') overrides.sosyal = questionCount;
    else if (name === 'fen') overrides.fen = questionCount;
  });

  return overrides;
};

export default function InternalExams() {
  const { systemConfig } = useAuth();
  const { showAlert } = useModal();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resultLoading, setResultLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [exams, setExams] = useState<PracticeExam[]>([]);
  const [selectedExam, setSelectedExam] = useState<PracticeExam | null>(null);
  const [reportData, setReportData] = useState<any>(null);
  const [totalStudents, setTotalStudents] = useState(0);
  const [examProgress, setExamProgress] = useState<{
    status: 'SAVED' | 'COMPLETED' | null;
    kitapcikTuru?: 'A' | 'B' | null;
    answers: Record<string, Array<string | null>>;
    branchSelections?: Partial<Record<'sosyal_bilimler' | 'sosyal_bilimler2', 'din' | 'felsefe'>>;
  } | null>(null);
  const [pendingSubmitPayload, setPendingSubmitPayload] = useState<ExamSolvePayload | null>(null);
  const [isSubmitActionModalOpen, setIsSubmitActionModalOpen] = useState(false);

  const fetchExams = async () => {
    try {
      setLoading(true);
      const res = await ApiService.getPracticeExams({ limit: 200, systemType: 'internal' });
      const rows = Array.isArray((res as any)?.rows) ? (res as any).rows : Array.isArray((res as any)?.data) ? (res as any).data : [];
      setExams(rows);
    } catch (error: any) {
      await showAlert({ title: 'Hata', message: error?.message || 'Denemeler yüklenirken bir hata oluştu.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExams();
  }, []);

  const hydrateReport = useMemo(() => {
    return (row: any, metadata: any) => {
      setReportData(getStudentReportData(row, metadata));
      setTotalStudents(metadata?.overallStats?.totalStudents || 0);
      setViewMode('result');
    };
  }, []);

  const handleSolveExam = async (exam: PracticeExam) => {
    try {
      setLoading(true);
      setSelectedExam(exam);
      setReportData(null);

      let progress: any = null;
      if (exam.hasSavedProgress) {
        try {
          const progressRes = await ApiService.getMyPracticeExamProgress(exam.id);
          progress = (progressRes as any)?.progress || null;
        } catch (error: any) {
          if (!/bulunamadı|ilerleme/i.test(String(error?.message || ''))) {
            throw error;
          }
        }
      }

      setExamProgress(progress ? {
        status: progress.status || null,
        kitapcikTuru: progress.kitapcikTuru || null,
        answers: progress.answers || {},
        branchSelections: progress.branchSelections || {},
      } : null);
      setViewMode('solve');
    } catch (error: any) {
      await showAlert({ title: 'Hata', message: error?.message || 'Kayıtlı cevaplar yüklenirken bir hata oluştu.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewResult = async (exam: PracticeExam) => {
    try {
      setResultLoading(true);
      setSelectedExam(exam);
      const res = await ApiService.getMyPracticeExamResult(exam.id);
      hydrateReport((res as any)?.result, {
        bookletMapping: (res as any)?.bookletMapping || {},
        testNames: (res as any)?.testNames || {},
        testAverages: (res as any)?.testAverages || {},
        overallStats: (res as any)?.overallStats || {},
      });
    } catch (error: any) {
      await showAlert({ title: 'Hata', message: error?.message || 'Sonuç yüklenirken bir hata oluştu.', type: 'error' });
    } finally {
      setResultLoading(false);
    }
  };

  const handleCompleteSubmit = async (payload: ExamSolvePayload) => {
    if (!selectedExam) return;

    try {
      setSubmitting(true);
      const res = await ApiService.submitInternalPracticeExam(selectedExam.id, payload);
      hydrateReport((res as any)?.result, {
        bookletMapping: (res as any)?.bookletMapping || {},
        testNames: (res as any)?.testNames || {},
        testAverages: (res as any)?.testAverages || {},
        overallStats: (res as any)?.overallStats || {},
      });

      setExams((current) =>
        current.map((exam) =>
          exam.id === selectedExam.id ? { ...exam, hasMyResult: true, hasSavedProgress: false, stats: true } : exam
        )
      );
      setExamProgress(null);
    } catch (error: any) {
      await showAlert({ title: 'Hata', message: error?.message || 'Deneme gönderilirken bir hata oluştu.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (payload: ExamSolvePayload) => {
    setPendingSubmitPayload(payload);
    setIsSubmitActionModalOpen(true);
  };

  const handleSaveAndExit = async () => {
    if (!selectedExam || !pendingSubmitPayload) return;

    try {
      setSubmitting(true);
      const res = await ApiService.saveInternalPracticeExam(selectedExam.id, pendingSubmitPayload);
      const progress = (res as any)?.progress || null;

      setExams((current) =>
        current.map((exam) =>
          exam.id === selectedExam.id ? { ...exam, hasSavedProgress: true, hasMyResult: false } : exam
        )
      );

      setExamProgress(progress ? {
        status: progress.status || null,
        kitapcikTuru: progress.kitapcikTuru || null,
        answers: progress.answers || {},
        branchSelections: progress.branchSelections || {},
      } : null);
      setIsSubmitActionModalOpen(false);
      setPendingSubmitPayload(null);
      setViewMode('list');
      setSelectedExam(null);
      await showAlert({ title: 'Kaydedildi', message: 'Cevaplarınız kaydedildi. Daha sonra kaldığınız yerden devam edebilirsiniz.', type: 'success' });
    } catch (error: any) {
      await showAlert({ title: 'Hata', message: error?.message || 'Cevaplar kaydedilirken bir hata oluştu.', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAndFinish = async () => {
    if (!pendingSubmitPayload) return;
    setIsSubmitActionModalOpen(false);
    await handleCompleteSubmit(pendingSubmitPayload);
    setPendingSubmitPayload(null);
  };

  const handleBack = () => {
    if (viewMode === 'result' && selectedExam) {
      setViewMode('list');
      setReportData(null);
      setSelectedExam(null);
      return;
    }

    setViewMode('list');
    setSelectedExam(null);
    setReportData(null);
    setExamProgress(null);
    setPendingSubmitPayload(null);
    setIsSubmitActionModalOpen(false);
  };

  const handlePrint = () => {
    window.print();
  };

  const questionCountOverrides = useMemo(() => resolveQuestionCountOverrides(selectedExam), [selectedExam]);

  if (viewMode === 'solve' && selectedExam) {
    return (
      <div className="px-6 pt-6 pb-1 space-y-4 h-full overflow-auto">
        <div className="flex items-center gap-4">
          <button onClick={handleBack} className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors">
            <ChevronLeft size={22} />
          </button>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{selectedExam.name}</h2>
            <p className="text-sm text-gray-500">
              {selectedExam.levelType === 'lgs'
                ? 'LGS optiği ile cevaplayın, kaydedin veya bitirin.'
                : selectedExam.yksMode === 'single_tyt'
                  ? 'TYT optiği ile cevaplayın, kaydedin veya bitirin.'
                  : 'YKS optiği ile cevaplayın, kaydedin veya bitirin.'}
            </p>
          </div>
        </div>

        {selectedExam.levelType === 'lgs' ? (
          <LGSOptikForm
            submitting={submitting}
            submitLabel="Sınavı Kaydet"
            questionCountOverrides={questionCountOverrides}
            answerKey={selectedExam.answerKey}
            initialAnswers={examProgress?.answers}
            initialKitapcikTuru={examProgress?.kitapcikTuru}
            onSubmit={({ answers, kitapcikTuru }) => handleSubmit({ answers, kitapcikTuru })}
          />
        ) : (
          <YKSOptikForm
            submitting={submitting}
            submitLabel="Sınavı Kaydet"
            questionCountOverrides={questionCountOverrides}
            availableSegments={selectedExam.availableSegments}
            answerKey={selectedExam.answerKey}
            initialAnswers={examProgress?.answers}
            initialKitapcikTuru={examProgress?.kitapcikTuru}
            initialBranchSelections={examProgress?.branchSelections}
            onSubmit={({ answers, kitapcikTuru, branchSelections }) => handleSubmit({ answers, kitapcikTuru, branchSelections })}
          />
        )}

        <Modal isOpen={isSubmitActionModalOpen} onClose={() => !submitting && setIsSubmitActionModalOpen(false)} title="Sınav işlemini seç" size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Cevaplarınızı kaydedip çıkabilir veya kaydedip sınavı tamamen bitirebilirsiniz.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={handleSaveAndExit}
                disabled={submitting}
                className="w-full rounded-[10px] border-[1.5px] border-[#f3c2d8] bg-[#fff7fb] px-[18px] py-[10px] text-[12px] font-bold uppercase tracking-[0.8px] text-[#c02d72] transition-all hover:bg-[#fff1f7] hover:border-[#e5a8c6] disabled:opacity-50"
              >
                {submitting ? 'Kaydediliyor...' : 'Kaydet ve Çık'}
              </button>
              <button
                type="button"
                onClick={handleSaveAndFinish}
                disabled={submitting}
                className="w-full rounded-[10px] border-0 bg-[linear-gradient(135deg,#d63384,#b5246a)] px-[28px] py-[10px] text-[13px] font-bold uppercase tracking-[1px] text-white shadow-[0_3px_12px_rgba(214,51,132,0.25)] transition-all hover:-translate-y-px hover:shadow-[0_5px_20px_rgba(214,51,132,0.35)] disabled:opacity-50"
              >
                {submitting ? 'İşleniyor...' : 'Kaydet ve Bitir'}
              </button>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  if (viewMode === 'result' && selectedExam && reportData) {
    return (
      <div className="p-6 space-y-6 h-full flex flex-col">
        <div className="flex items-center justify-between no-print">
          <div className="flex items-center gap-4">
            <button onClick={handleBack} className="p-2 rounded-full hover:bg-gray-100 text-gray-600 transition-colors">
              <ChevronLeft size={22} />
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{selectedExam.name}</h2>
              <p className="text-sm text-gray-500">Sınav sonucunuz ve karneniz</p>
            </div>
          </div>

          <button
            onClick={handlePrint}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <Printer size={16} />
            Yazdır / İndir
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4 flex justify-center">
          <div className="bg-white shadow-2xl rounded-sm print:shadow-none print:w-full" style={{ width: '210mm', minHeight: '297mm', padding: '5mm' }}>
            <StudentReportCard
              data={reportData}
              examName={selectedExam?.name}
              companyLogo={systemConfig?.companyLogo || undefined}
              companyName={systemConfig?.companyName || undefined}
              totalStudents={totalStudents}
            />
          </div>

          <div className="hidden print:block print:fixed print:inset-0 print:bg-white print:z-[99999]">
            <div className="print-page-break w-[210mm] h-[297mm] relative mx-auto bg-white p-8 box-border">
              <StudentReportCard
                data={reportData}
                examName={selectedExam?.name}
                isPrint
                companyLogo={systemConfig?.companyLogo || undefined}
                companyName={systemConfig?.companyName || undefined}
                totalStudents={totalStudents}
              />
            </div>
          </div>
        </div>

        <style>{`
          @media print {
            @page {
              size: A4;
              margin: 0;
            }

            html, body {
              background: white !important;
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
              margin: 0 !important;
              padding: 0 !important;
              overflow: visible !important;
              height: auto !important;
            }

            aside,
            header,
            nav,
            .no-print {
              display: none !important;
            }

            .print-page-break {
              page-break-after: always;
              break-after: page;
              padding: 10mm !important;
              box-sizing: border-box !important;
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full overflow-auto">
      <div>
        <h2 className="text-xl font-bold text-gray-900">Denemeler</h2>
        <p className="text-sm text-gray-500">Akademi sistemi ile eklenen denemeleri çözebilir ve sonucunuzu görebilirsiniz.</p>
      </div>

      {loading || resultLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : exams.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-12 text-center border-2 border-dashed border-gray-200">
          <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Henüz Yayında Deneme Yok</h3>
          <p className="text-gray-500 mt-1">Akademi sistemi ile oluşturulan denemeler burada görünecek.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {exams.map((exam) => (
            <div
              key={exam.id}
              className="group bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg transition-all relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity">
                <Award className="w-16 h-16 text-blue-600 transform rotate-12" />
              </div>

              <div className="flex items-center gap-2 mb-3">
                <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${exam.levelType === 'lgs' ? 'bg-emerald-50 text-emerald-700' : 'bg-indigo-50 text-indigo-700'}`}>
                  {exam.levelType === 'lgs' ? 'LGS' : exam.yksMode === 'single_tyt' ? 'TYT' : 'YKS'}
                </span>
                {exam.hasSavedProgress && !exam.hasMyResult && (
                  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-amber-50 text-amber-700">
                    Kayıtlı İlerleme
                  </span>
                )}
                {exam.hasMyResult && (
                  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-blue-50 text-blue-700">
                    Cevaplandı
                  </span>
                )}
                {exam.hasCorrectAnswers === false && (
                  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold bg-amber-50 text-amber-700">
                    Değerlendirme Kapalı
                  </span>
                )}
              </div>

              <h3 className="text-lg font-semibold text-gray-800 line-clamp-2" title={exam.name}>
                {exam.name}
              </h3>

              <p className="text-sm text-gray-500 mb-4 flex items-center gap-2">
                <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                  {exam.date ? new Date(exam.date).toLocaleDateString('tr-TR') : '-'}
                </span>
              </p>

              <div className="space-y-2">
                <div className={`grid gap-2 ${exam.hasMyResult ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  <button
                    onClick={() => handleSolveExam(exam)}
                    disabled={exam.hasCorrectAnswers === false}
                    className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm font-medium ${exam.hasCorrectAnswers === false
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                  >
                    <PenSquare size={16} />
                    {exam.hasSavedProgress && !exam.hasMyResult ? 'Devam Et' : exam.hasMyResult ? 'Cevapla' : 'Denemeyi Cevapla'}
                  </button>

                  {exam.hasMyResult && (
                    <button
                      onClick={() => handleViewResult(exam)}
                      className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 transition-colors text-sm font-medium"
                    >
                      <FileSpreadsheet size={16} />
                      Sonucu Gör
                    </button>
                  )}
                </div>

                {exam.hasCorrectAnswers === false && (
                  <p className="text-xs text-amber-700">
                    Bu denemede doğru seçenekler tanımlı değil.
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}